import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, X, MessageSquare } from 'lucide-react';

const SUGGESTIONS = [
  "What's the biggest risk on this deal?",
  "Summarize recent activity",
  "What should I do next?",
  "Draft a follow-up talking point",
];

export default function DealChat({ opportunity, detail, industry }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `deal-${opportunity.id}-${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const buildContext = () => ({
    accountName: opportunity.accountName,
    dealValue: opportunity.dealValue,
    stage: detail.stage,
    closeDate: detail.closeDate,
    pocName: detail.poc.name,
    dealSummary: detail.dealSummary,
    pendingActions: detail.pendingActions?.items?.map(a => `${a.label}: ${a.description}`).join(' | ') || 'None',
    timeline: detail.timeline?.map(e => `[${e.time}] ${e.title}: ${e.description}`).join(' | ') || 'No activity',
    contacts: detail.contacts?.map(c => `${c.name} (${c.title})`).join(', ') || 'None',
    riskSignal: opportunity.riskSignal || 'None',
    industry: industry,
  });

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          session_id: sessionId,
          context: buildContext(),
        }),
      });
      const data = await res.json();
      if (data.response) {
        setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Try again.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Connection error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
        data-testid="deal-chat-trigger"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="text-sm font-semibold">Ask about this deal</span>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" data-testid="deal-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-4.5 h-4.5" />
          <div>
            <p className="text-sm font-semibold leading-tight">Forsyt AI</p>
            <p className="text-[11px] text-blue-100 leading-tight">{opportunity.accountName}</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors" data-testid="deal-chat-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="deal-chat-messages">
        {messages.length === 0 && (
          <div className="space-y-4 pt-2">
            <div className="bg-blue-50 rounded-xl p-3.5">
              <p className="text-sm text-blue-800 font-medium mb-1">Hi! I know everything about the {opportunity.accountName} deal.</p>
              <p className="text-xs text-blue-600">Try asking me a question or pick one below:</p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s)}
                  className="text-left text-sm px-3.5 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-blue-300 transition-all duration-150"
                  data-testid={`chat-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-md'
                  : 'bg-gray-100 text-gray-800 rounded-bl-md'
              }`}
              data-testid={`chat-msg-${i}`}
            >
              {msg.text}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-xl rounded-bl-md px-4 py-3 flex items-center gap-1.5" data-testid="chat-loading">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 px-4 py-3 border-t border-gray-100 flex-shrink-0 bg-white" data-testid="deal-chat-input-form">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about this deal..."
          className="flex-1 text-sm px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={loading}
          data-testid="deal-chat-input"
        />
        <button
          type="submit"
          disabled={!input.trim() || loading}
          className="p-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl transition-colors flex-shrink-0"
          data-testid="deal-chat-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
